import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { Button, Label, TextInput, Alert } from "flowbite-react";
import { UserCog, KeyRound } from "lucide-react";
import {useLocation} from "react-router-dom"
const UserSettings = () => {
  const location = useLocation()
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPassword,
    watch,
    formState: { errors: passwordErrors },
  } = useForm();

  const newPassword = watch("newPassword");
  const outsideUser = location?.state?.userId
  const inside = outsideUser ? "false" : "true"
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${
            process.env.REACT_APP_BACKEND_URL
          }getUserSpecificInfo/?inside=${inside}&userId=${outsideUser}`,
          { withCredentials: true }
        );
        setUserInfo(response.data.data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load user information");
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const onPasswordSubmit = async (data) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}updateProfile`,
        {
          userId : outsideUser,
          inside : inside,
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
        },
        { withCredentials: true }
      );
      setSuccess("Password updated successfully!");
      resetPassword();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update password");
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {error && (
        <Alert color="failure" className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" className="mb-4">
          {success}
        </Alert>
      )}

      <div className="grid gap-6 mb-6">
        <div className="p-6 bg-white rounded-lg shadow-md">
          <div className="flex items-center gap-2 mb-6">
            <UserCog className="h-6 w-6" />
            <h2 className="text-xl font-semibold">Profile Information</h2>
          </div>

          <form className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {userInfo?.owner && (
                <div>
                  <Label htmlFor="owner" value="Full Name" />
                  <TextInput id="owner" defaultValue={userInfo?.owner} />
                </div>
              )}
              {userInfo?.FirstName && (
                <div>
                  <Label htmlFor="firstName" value="First Name" />
                  <TextInput
                    id="firstName"
                    defaultValue={userInfo?.FirstName}
                  />
                </div>
              )}

              {userInfo?.LastName && (
                <div>
                  <Label htmlFor="lastName" value="Last Name" />
                  <TextInput id="lastName" defaultValue={userInfo?.LastName} />
                </div>
              )}

              {userInfo?.Email && (
                <div>
                  <Label htmlFor="email" value="Email" />
                  <TextInput
                    id="email"
                    type="email"
                    defaultValue={userInfo?.Email}
                  />
                </div>
              )}

              {userInfo?.PhoneNumber && (
                <div>
                  <Label htmlFor="phoneNumber" value="Phone Number" />
                  <TextInput
                    id="phoneNumber"
                    defaultValue={userInfo?.PhoneNumber}
                  />
                </div>
              )}

              {userInfo?.CIN && (
                <div>
                  <Label htmlFor="cin" value="CIN" />
                  <TextInput defaultValue={userInfo?.CIN} />
                </div>
              )}

              {userInfo?.username && (
                <div>
                  <Label htmlFor="username" value="Username" />
                  <TextInput id="username" defaultValue={userInfo?.username} />
                </div>
              )}
            </div>
          </form>
        </div>

        <div className="p-6 bg-white rounded-lg shadow-md">
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="h-6 w-6" />
            <h2 className="text-xl font-semibold">Change Password</h2>
          </div>

          <form
            onSubmit={handlePasswordSubmit(onPasswordSubmit)}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="oldPassword" value="Current Password" />
              <TextInput
                id="oldPassword"
                type="password"
                {...registerPassword("oldPassword", {
                  required: "Current password is required",
                })}
                helperText={passwordErrors.oldPassword?.message}
                color={passwordErrors.oldPassword ? "failure" : "gray"}
              />
            </div>

            <div>
              <Label htmlFor="newPassword" value="New Password" />
              <TextInput
                id="newPassword"
                type="password"
                {...registerPassword("newPassword", {
                  required: "New password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters",
                  },
                })}
                helperText={passwordErrors.newPassword?.message}
                color={passwordErrors.newPassword ? "failure" : "gray"}
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" value="Confirm New Password" />
              <TextInput
                id="confirmPassword"
                type="password"
                {...registerPassword("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) =>
                    value === newPassword || "Passwords do not match",
                })}
                helperText={passwordErrors.confirmPassword?.message}
                color={passwordErrors.confirmPassword ? "failure" : "gray"}
              />
            </div>

            <Button type="submit" className="mt-4" color={"dark"}>
              <KeyRound className="h-4 w-4 mr-2" />
              Change Password
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
